import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../api/queryClient';
import { userService } from '../services/userService';
import { useDebouncedValue } from './useDebouncedValue';

export function useAdminUsers() {
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [role, setRole] = useState('all');
  const [limit, setLimit] = useState(50);
  const debouncedQ = useDebouncedValue(q, 300);

  useEffect(() => {
    setLimit(50);
  }, [debouncedQ, role]);

  const filters = {
    q: debouncedQ || undefined,
    role: role !== 'all' ? role : undefined,
    limit,
  };
  const queryKey = queryKeys.adminUsers(filters);

  const query = useQuery({
    queryKey,
    queryFn: () => userService.list(filters),
  });

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
    [queryClient],
  );

  const run = useCallback(
    async (action) => {
      const result = await action();
      await invalidate();
      return result;
    },
    [invalidate],
  );

  const createMutation = useMutation({
    mutationFn: (body) => run(() => userService.create(body)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }) => run(() => userService.update(id, body)),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, password }) =>
      run(() => userService.resetPassword(id, password)),
  });

  return {
    items: query.data?.items ?? [],
    meta: query.data?.meta ?? null,
    loading: query.isLoading || (query.isFetching && !(query.data?.items?.length)),
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : 'Failed to load users'
      : null,
    q,
    setQ,
    role,
    setRole,
    loadMore: () => setLimit((n) => n + 50),
    reload: () => query.refetch(),
    create: (body) => createMutation.mutateAsync(body),
    update: (id, body) => updateMutation.mutateAsync({ id, body }),
    resetPassword: (id, password) =>
      resetPasswordMutation.mutateAsync({ id, password }),
    busy:
      createMutation.isPending ||
      updateMutation.isPending ||
      resetPasswordMutation.isPending,
  };
}
